import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import AddIcon from "@mui/icons-material/Add";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { makeid } from "../../utility";
import NodiantCalendar from "../calendar/NodiantCalendar";
import DialogWithTextInput from "../DialogWithTextInput";
import ToolbarWithLogo from "../ToolbarWithLogo";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: "0 8px 28px rgba(20, 35, 25, 0.065)",
};

function formatDate(value) {
    const date = dayjs(value);
    return date.isValid() ? date.format("D MMM YYYY") : "Date unavailable";
}

function CalendarFilter({ date, label, onChange, onClear }) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2.5,
                borderColor: "divider",
                boxShadow: "none",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    minHeight: 58,
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.subtle",
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 1.5,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <CalendarMonthOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {date ? formatDate(date) : "Any date"}
                    </Typography>
                </Box>
                {date && (
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<ClearOutlinedIcon />}
                        onClick={onClear}
                    >
                        Clear
                    </Button>
                )}
            </Box>
            <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
                <NodiantCalendar
                    selectedDate={date || null}
                    onDateChange={onChange}
                />
            </Box>
        </Card>
    );
}

function SurveyRow({ survey, creatorName, onEdit, onResults, onTake }) {
    return (
        <Box
            sx={{
                p: { xs: 2, sm: 2.25 },
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                transition: "background-color 120ms ease",
                "&:hover": { backgroundColor: "action.hover" },
            }}
        >
            <Box sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 2,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <AssignmentOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 800,
                            lineHeight: 1.25,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {survey.title || "Untitled survey"}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                    >
                        By {creatorName} · Updated{" "}
                        {formatDate(survey.lastUpdated)}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    width: { xs: "100%", md: "auto" },
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    pl: { xs: 7.25, md: 0 },
                }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditNoteOutlinedIcon />}
                    onClick={onEdit}
                    disabled={!survey.canEdit}
                >
                    Edit questions
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<BarChartOutlinedIcon />}
                    onClick={onResults}
                    disabled={!survey.canViewResults}
                >
                    View results
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<FactCheckOutlinedIcon />}
                    onClick={onTake}
                    disabled={!survey.canTake}
                >
                    Take survey
                </Button>
            </Box>
        </Box>
    );
}

function SurveySection({
    title,
    description,
    surveys,
    emptyMessage,
    userByKey,
    onEdit,
    onResults,
    onTake,
}) {
    return (
        <Card variant="outlined" sx={surfaceSx}>
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="h2">
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                    >
                        {description}
                    </Typography>
                </Box>
                <Chip
                    size="small"
                    label={surveys.length}
                    sx={{
                        minWidth: 34,
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                        color: "primary.main",
                    }}
                />
            </Box>
            <Divider />

            {surveys.length === 0 ? (
                <Box
                    sx={{
                        minHeight: 170,
                        px: 3,
                        py: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                        textAlign: "center",
                    }}
                >
                    <InboxOutlinedIcon
                        sx={{ mb: 1, fontSize: 40, opacity: 0.42 }}
                    />
                    <Typography variant="subtitle1" color="text.primary">
                        {emptyMessage}
                    </Typography>
                </Box>
            ) : (
                surveys.map((survey, index) => (
                    <React.Fragment key={survey.key}>
                        {index > 0 && <Divider />}
                        <SurveyRow
                            survey={survey}
                            creatorName={
                                userByKey.get(survey.createdBy)?.fullName ||
                                "Unknown user"
                            }
                            onEdit={onEdit(survey)}
                            onResults={onResults(survey)}
                            onTake={onTake(survey)}
                        />
                    </React.Fragment>
                ))
            )}
        </Card>
    );
}

export default function SurveySelectPage() {
    const { currentUserData } = useContext(UserContext);
    const {
        surveyInfo = [],
        surveyInfoLoaded = false,
        users = [],
        groups = [],
        groupsLoaded = false,
    } = useContext(DatabaseContext);
    const history = useHistory();

    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [dateSearchEnabled, setDateSearchEnabled] = useState(false);
    const [searchedSurveys, setSearchedSurveys] = useState([]);
    const [surveyNameDialogOpen, setSurveyNameDialogOpen] = useState(false);
    const [searchResultsVisible, setSearchResultsVisible] = useState(false);
    const [surveyNameSearch, setSurveyNameSearch] = useState("");
    const [userNameSearch, setUserNameSearch] = useState("");

    const userByKey = useMemo(
        () => new Map(users.map((user) => [user.key, user])),
        [users],
    );

    const { availableSurveys, mySurveys, surveysSharedWithMe } = useMemo(() => {
        const emptySurveyLists = {
            availableSurveys: [],
            mySurveys: [],
            surveysSharedWithMe: [],
        };

        if (!currentUserData) {
            return emptySurveyLists;
        }

        const mySurveysArray = [];
        const sharedSurveysArray = [];
        const availableSurveysArray = [];

        surveyInfo.forEach((survey) => {
            const surveyWithPermissions = {
                ...survey,
                canEdit: false,
                canViewResults: false,
                canTake: false,
            };

            if (survey.createdBy === currentUserData.key) {
                surveyWithPermissions.canEdit = true;
                surveyWithPermissions.canViewResults = true;
                surveyWithPermissions.canTake = true;
                mySurveysArray.push(surveyWithPermissions);
                availableSurveysArray.push(surveyWithPermissions);
                return;
            }

            const matchingUser = (survey.users || []).find(
                (user) => user.key === currentUserData.key,
            );

            if (matchingUser) {
                surveyWithPermissions.canEdit = Boolean(matchingUser.canEdit);
                surveyWithPermissions.canViewResults = Boolean(
                    matchingUser.canViewResults,
                );
                surveyWithPermissions.canTake = true;
            }

            (survey.groups || []).forEach((surveyGroup) => {
                const matchingGroup = groups.find(
                    (group) => group.key === surveyGroup.key,
                );
                const isMember = (matchingGroup?.users || []).some(
                    (user) => user.key === currentUserData.key,
                );

                if (isMember) {
                    surveyWithPermissions.canEdit =
                        surveyWithPermissions.canEdit ||
                        Boolean(surveyGroup.canEdit);
                    surveyWithPermissions.canViewResults =
                        surveyWithPermissions.canViewResults ||
                        Boolean(surveyGroup.canViewResults);
                    surveyWithPermissions.canTake = true;
                }
            });

            if (
                surveyWithPermissions.canEdit ||
                surveyWithPermissions.canViewResults ||
                surveyWithPermissions.canTake
            ) {
                sharedSurveysArray.push(surveyWithPermissions);
                availableSurveysArray.push(surveyWithPermissions);
            }
        });

        return {
            availableSurveys: availableSurveysArray,
            mySurveys: mySurveysArray,
            surveysSharedWithMe: sharedSurveysArray,
        };
    }, [currentUserData, groups, surveyInfo]);

    const canCreateSurveys = Boolean(
        currentUserData?.permissions?.createSurveys,
    );
    const isSurveysLoading =
        !currentUserData || !groupsLoaded || !surveyInfoLoaded;

    const handleSurveyQuestionsSelect = (survey) => () => {
        history.push(`/surveys/e?key=${survey.key}`);
    };

    const handleSurveyAnswersSelect = (survey) => () => {
        history.push(`/surveys/a?key=${survey.key}`);
    };

    const handleTakeSurveySelect = (survey) => () => {
        history.push(`/surveys/r?key=${survey.key}`);
    };

    function handleCloseSurveyNameDialog(newName, okPressed) {
        if (!okPressed) {
            setSurveyNameDialogOpen(false);
            return;
        }

        const surveyName = newName.trim();

        if (surveyName !== "") {
            const infoRef = firebase.database().ref("surveyinfo");
            const key = makeid(15);

            infoRef.child(key).set({
                createdBy: currentUserData.key,
                createdOn: dayjs().toString(),
                lastUpdated: dayjs().toString(),
                intro: "",
                title: surveyName,
                anonymous: true,
            });

            setSurveyNameDialogOpen(false);
            history.push(`/surveys/e?key=${key}`);
        }
    }

    function handleSearchPressed() {
        const surveyNameTerm = surveyNameSearch.trim().toLowerCase();
        const userNameTerm = userNameSearch.trim().toLowerCase();
        const fromBoundary =
            dateSearchEnabled && fromDate
                ? dayjs(fromDate).startOf("day")
                : null;
        const toBoundary =
            dateSearchEnabled && toDate ? dayjs(toDate).endOf("day") : null;

        const searchResults = availableSurveys.filter((survey) => {
            if (
                surveyNameTerm &&
                !(survey.title || "").toLowerCase().includes(surveyNameTerm)
            ) {
                return false;
            }

            const creatorName = userByKey.get(survey.createdBy)?.fullName || "";
            if (
                userNameTerm &&
                !creatorName.toLowerCase().includes(userNameTerm)
            ) {
                return false;
            }

            const lastUpdated = dayjs(survey.lastUpdated);
            if (fromBoundary && lastUpdated.isBefore(fromBoundary)) {
                return false;
            }
            if (toBoundary && lastUpdated.isAfter(toBoundary)) {
                return false;
            }

            return true;
        });

        setSearchedSurveys(searchResults);
        setSearchResultsVisible(true);
    }

    function handleClearSearch() {
        setSurveyNameSearch("");
        setUserNameSearch("");
        setDateSearchEnabled(false);
        setFromDate(undefined);
        setToDate(undefined);
        setSearchedSurveys([]);
        setSearchResultsVisible(false);
    }

    const hasSearchFilters = Boolean(
        surveyNameSearch ||
        userNameSearch ||
        (dateSearchEnabled && (fromDate || toDate)),
    );

    function handleDateSearchToggle(event) {
        const enabled = event.target.checked;
        setDateSearchEnabled(enabled);

        if (!enabled) {
            setFromDate(undefined);
            setToDate(undefined);
        }
    }

    return (
        <>
            <Box
                sx={{
                    minHeight: {
                        xs: "calc(100dvh - 60px)",
                        sm: "calc(100vh - 128px)",
                    },
                    pb: 12,
                    backgroundColor: "background.default",
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                        }}
                    >
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    p: { xs: 2.5, sm: 3 },
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    "&:last-child": { pb: { xs: 2.5, sm: 3 } },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 46,
                                        height: 46,
                                        display: "grid",
                                        placeItems: "center",
                                        flexShrink: 0,
                                        borderRadius: 2,
                                        color: "primary.main",
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.09)",
                                    }}
                                >
                                    <AssignmentOutlinedIcon />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="h5"
                                        component="h1"
                                        sx={{ lineHeight: 1.1 }}
                                    >
                                        Surveys
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        Find, complete, and manage the surveys
                                        available to you.
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    label={
                                        isSurveysLoading
                                            ? "Loading…"
                                            : `${availableSurveys.length} available`
                                    }
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.09)",
                                        color: "primary.main",
                                    }}
                                />
                                {!isSurveysLoading && canCreateSurveys && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                            setSurveyNameDialogOpen(true)
                                        }
                                    >
                                        New survey
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        {isSurveysLoading ? (
                            <Card variant="outlined" sx={surfaceSx}>
                                <Box
                                    sx={{
                                        minHeight: 320,
                                        px: 3,
                                        py: 5,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1.5,
                                        color: "text.secondary",
                                        textAlign: "center",
                                    }}
                                >
                                    <CircularProgress
                                        size={32}
                                        aria-label="Loading surveys"
                                    />
                                    <Typography variant="body2">
                                        Loading your surveys…
                                    </Typography>
                                </Box>
                            </Card>
                        ) : (
                            <>
                                <Card variant="outlined" sx={surfaceSx}>
                                    <Box
                                        sx={{ px: { xs: 2, sm: 3 }, py: 2.25 }}
                                    >
                                        <Typography variant="h6" component="h2">
                                            Search surveys
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Filter by title, creator, or the
                                            date a survey was last updated.
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <Box
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "minmax(0, 1fr)",
                                                    sm: "repeat(2, minmax(0, 1fr))",
                                                },
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                value={surveyNameSearch}
                                                id="surveyNameSearch"
                                                onChange={(event) =>
                                                    setSurveyNameSearch(
                                                        event.target.value,
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        handleSearchPressed();
                                                    }
                                                }}
                                                label="Survey name"
                                            />
                                            <TextField
                                                fullWidth
                                                value={userNameSearch}
                                                id="userNameSearch"
                                                onChange={(event) =>
                                                    setUserNameSearch(
                                                        event.target.value,
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        handleSearchPressed();
                                                    }
                                                }}
                                                label="Created by"
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                minHeight: 64,
                                                px: 2,
                                                py: 1.25,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2.5,
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    flexShrink: 0,
                                                    borderRadius: 1.5,
                                                    color: "primary.main",
                                                    backgroundColor:
                                                        "rgba(0, 149, 61, 0.09)",
                                                }}
                                            >
                                                <CalendarMonthOutlinedIcon fontSize="small" />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 800 }}
                                                >
                                                    Search by date
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Filter surveys by when they
                                                    were last updated.
                                                </Typography>
                                            </Box>
                                            <Switch
                                                checked={dateSearchEnabled}
                                                onChange={
                                                    handleDateSearchToggle
                                                }
                                                inputProps={{
                                                    "aria-label":
                                                        "Search surveys by date",
                                                }}
                                            />
                                        </Box>

                                        {dateSearchEnabled && (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "minmax(0, 1fr)",
                                                        md: "repeat(2, minmax(0, 1fr))",
                                                    },
                                                    gap: 2,
                                                }}
                                            >
                                                <CalendarFilter
                                                    label="Updated from"
                                                    date={fromDate}
                                                    onChange={setFromDate}
                                                    onClear={() =>
                                                        setFromDate(undefined)
                                                    }
                                                />
                                                <CalendarFilter
                                                    label="Updated to"
                                                    date={toDate}
                                                    onChange={setToDate}
                                                    onClear={() =>
                                                        setToDate(undefined)
                                                    }
                                                />
                                            </Box>
                                        )}

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                flexWrap: "wrap",
                                                gap: 1,
                                            }}
                                        >
                                            {hasSearchFilters && (
                                                <Button
                                                    color="inherit"
                                                    startIcon={
                                                        <ClearOutlinedIcon />
                                                    }
                                                    onClick={handleClearSearch}
                                                >
                                                    Clear filters
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                startIcon={
                                                    <SearchOutlinedIcon />
                                                }
                                                onClick={handleSearchPressed}
                                            >
                                                Search
                                            </Button>
                                        </Box>
                                    </Box>
                                </Card>

                                {searchResultsVisible && (
                                    <SurveySection
                                        title="Search results"
                                        description="Surveys matching your selected filters."
                                        surveys={searchedSurveys}
                                        emptyMessage="No surveys match these filters"
                                        userByKey={userByKey}
                                        onEdit={handleSurveyQuestionsSelect}
                                        onResults={handleSurveyAnswersSelect}
                                        onTake={handleTakeSurveySelect}
                                    />
                                )}

                                {mySurveys.length > 0 && (
                                    <SurveySection
                                        title="My surveys"
                                        description="Surveys you created and can manage."
                                        surveys={mySurveys}
                                        emptyMessage="You have not created any surveys"
                                        userByKey={userByKey}
                                        onEdit={handleSurveyQuestionsSelect}
                                        onResults={handleSurveyAnswersSelect}
                                        onTake={handleTakeSurveySelect}
                                    />
                                )}

                                {surveysSharedWithMe.length > 0 && (
                                    <SurveySection
                                        title="Shared with me"
                                        description="Surveys shared with you directly or through a group."
                                        surveys={surveysSharedWithMe}
                                        emptyMessage="No surveys have been shared with you"
                                        userByKey={userByKey}
                                        onEdit={handleSurveyQuestionsSelect}
                                        onResults={handleSurveyAnswersSelect}
                                        onTake={handleTakeSurveySelect}
                                    />
                                )}

                                {mySurveys.length === 0 &&
                                    surveysSharedWithMe.length === 0 && (
                                        <Card variant="outlined" sx={surfaceSx}>
                                            <Box
                                                sx={{
                                                    minHeight: 220,
                                                    px: 3,
                                                    py: 5,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    textAlign: "center",
                                                    color: "text.secondary",
                                                }}
                                            >
                                                <InboxOutlinedIcon
                                                    sx={{
                                                        mb: 1.25,
                                                        fontSize: 44,
                                                        opacity: 0.42,
                                                    }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    color="text.primary"
                                                >
                                                    No surveys available
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {canCreateSurveys
                                                        ? "Create a survey to get started."
                                                        : "Surveys shared with you will appear here."}
                                                </Typography>
                                                {canCreateSurveys && (
                                                    <Button
                                                        sx={{ mt: 2 }}
                                                        variant="contained"
                                                        disableElevation
                                                        startIcon={<AddIcon />}
                                                        onClick={() =>
                                                            setSurveyNameDialogOpen(
                                                                true,
                                                            )
                                                        }
                                                    >
                                                        New survey
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    )}
                            </>
                        )}
                    </Box>
                </Container>
            </Box>

            <ToolbarWithLogo />
            <DialogWithTextInput
                open={surveyNameDialogOpen}
                onClose={handleCloseSurveyNameDialog}
                title="Create survey"
                message="Give your survey a clear name. You can add the introduction and questions next."
                initialValue=""
                inputLabel="Survey name"
                confirmLabel="Create survey"
                headerIcon={<AssignmentOutlinedIcon />}
                maxLength={120}
                modern
            />
        </>
    );
}
